import { useSyncExternalStore } from 'react';
import { API } from './EsConstants';
import { useEsCcuPlace } from './EsFlags';
import { useEsStrings } from './EsStrings';

/**
 * The live concurrent-users count (switchEsCcu), where variantEsCcuPlace puts it: the
 * nav bar's control row, the account menu, or the footer. Each of the three asks for its own
 * place, so the count is drawn once whichever it is.
 *
 * <p>One socket for the page, esback's EsCcuSocket, open only while a drawn count is
 * listening: esback pushes the count on connect and whenever its 10 s recount moves it, so a
 * number changes in front of the reader with no reload and no poll. A ping every 30 s keeps the
 * ALB, which closes a connection idle for 60 s, from closing a socket on a steady count. A closed
 * socket is opened again after 5 s, doubling to a minute: esback answers 404 to a reader the
 * switch does not serve, and that reader must not knock every few seconds.
 */

export const API_CCU = API + 'ccu';

const PING_MS = 30000;
const RETRY_FIRST_MS = 5000;
const RETRY_MOST_MS = 60000;

let count = null;
let socket = null;
let ping = null;
let retry = null;
let retryMs = RETRY_FIRST_MS;
const listeners = new Set();

const socketUrl = () => ( window.location.protocol === 'https:' ? 'wss://' : 'ws://' ) + window.location.host + API_CCU;

function set( pCount ) {
    if ( count === pCount ) return;
    count = pCount;
    listeners.forEach( ( aListener ) => aListener() );
}

function open() {
    let aSocket;
    try {
        aSocket = new WebSocket( socketUrl() );
    }
    catch {
        later();
        return;
    }
    socket = aSocket;
    aSocket.onopen = () => {
        retryMs = RETRY_FIRST_MS;
        ping = setInterval( () => aSocket.readyState === 1 && aSocket.send( JSON.stringify( { type: 'ping' } ) ), PING_MS );
    };
    aSocket.onmessage = ( pEvent ) => {
        try {
            const aFrame = JSON.parse( pEvent.data );
            if ( aFrame?.type === 'count' && Number.isFinite( aFrame.count ) ) set( aFrame.count );
        }
        catch {
            // Not a frame this page reads.
        }
    };
    aSocket.onclose = () => {
        clearInterval( ping );
        ping = null;
        if ( socket !== aSocket ) return;
        socket = null;
        later();
    };
}

function later() {
    if ( listeners.size === 0 || retry ) return;
    retry = setTimeout( () => {
        retry = null;
        if ( listeners.size > 0 && !socket ) open();
    }, retryMs );
    retryMs = Math.min( retryMs * 2, RETRY_MOST_MS );
}

function shut() {
    clearTimeout( retry );
    clearInterval( ping );
    retry = null;
    ping = null;
    retryMs = RETRY_FIRST_MS;
    const aSocket = socket;
    socket = null;
    aSocket?.close();
    set( null );
}

function subscribe( pListener ) {
    listeners.add( pListener );
    if ( !socket && !retry ) open();
    return () => {
        listeners.delete( pListener );
        if ( listeners.size === 0 ) shut();
    };
}

const subscribeNothing = () => () => {};

/** The count, or null until one arrives; nothing is opened while pOn is false. */
export function useEsCcuCount( pOn ) {
    return useSyncExternalStore( pOn ? subscribe : subscribeNothing, () => ( pOn ? count : null ), () => null );
}

const TESTIDS = { nav: 'es-nav-ccu', menu: 'es-nav-account-ccu', footer: 'es-footer-ccu' };

// A readout, not a control, so it sits pressed into the bar rather than raised off it (Tim, 2026-10-27).
// In the menu it is plain text under a hairline instead.
const INSET = 'inline-block whitespace-nowrap rounded-full bg-black/10 px-3 py-0.5 ring-1 ring-black/20 dark:bg-white/10 dark:ring-white/20'
    + ' shadow-[inset_0_2px_4px_rgb(0_0_0/0.5)]';

/**
 * The count, drawn here when this is its place. `where` is nav, menu, or footer; `signedIn` is the
 * caller's auth, since the socket is an account's. `rowFolded` is the nav bar's fold: the menu takes
 * the row's count while the row cannot fit it (EsNavBar.js).
 */
export default function EsCcuCount( { where, signedIn, rowFolded = false, className = '' } ) {
    const t = useEsStrings();
    const place = useEsCcuPlace();
    const shown = Boolean( signedIn ) && ( place === where || ( rowFolded && where === 'menu' && place === 'nav' ) );
    const aCount = useEsCcuCount( shown );

    // One is the reader alone; nothing to report (Tim, 2026-10-27).
    if ( !shown || aCount === null || aCount <= 1 ) return null;

    // CCU on screen (Tim, 2026-10-27: "Use CCU for new display"); the words for a screen reader.
    const aShown = aCount.toLocaleString();
    const aText = t( 'nav-bar-ccu', 'CCU {count}', { count: aShown } );
    const aLabel = t( 'nav-bar-ccu-label', '{count} concurrent users', { count: aShown } );
    const aTip = t( 'nav-bar-ccu-tip', 'Concurrent users: active in the last five minutes' );
    return where === 'menu'
        ? <li data-testid={ TESTIDS.menu } title={ aTip } aria-label={ aLabel } className={ 'mt-1 border-t border-edge px-4 pt-1.5 text-center text-sm text-muted' + ( className ? ' ' + className : '' ) }>{ aText }</li>
        : <span data-testid={ TESTIDS[ where ] } data-tip={ aTip } aria-label={ aLabel } className={ INSET + ( className ? ' ' + className : '' ) }>{ aText }</span>;
}
